import { Job, JobStatus, SupabaseJob, formatDateTimeDisplay, formatSalary } from '@applicator/shared'

// Helper function to format days ago
export function formatDaysAgo(diffDays: number): string {
  return diffDays === 1 ? 'a day ago' : `${diffDays} days ago`
}

// Helper function to format hours ago
export function formatHoursAgo(diffHours: number): string {
  return diffHours === 1 ? 'an hour ago' : `${diffHours} hours ago`
}

// Helper function to format minutes ago
export function formatMinutesAgo(diffMinutes: number): string {
  return diffMinutes === 1 ? 'a minute ago' : `${diffMinutes} minutes ago`
}

// Helper function to format time difference
export function formatTimeDifference(diffDays: number, diffHours: number, diffMinutes: number): string {
  if (diffDays > 0) {
    return formatDaysAgo(diffDays)
  }
  if (diffHours > 0) {
    return formatHoursAgo(diffHours)
  }
  if (diffMinutes > 0) {
    return formatMinutesAgo(diffMinutes)
  }
  return 'just now'
}

// Helper function to calculate time differences
export function calculateTimeDifferences(diffMs: number) {
  const diffSeconds = Math.floor(diffMs / 1000)
  const diffMinutes = Math.floor(diffSeconds / 60)
  const diffHours = Math.floor(diffMinutes / 60)
  const diffDays = Math.floor(diffHours / 24)
  return { diffDays, diffHours, diffMinutes }
}

// Helper function to validate date string
export function isValidDateString(savedAt: string | undefined | null): boolean {
  return !!(savedAt && typeof savedAt === 'string')
}

// Helper function to create valid date
export function createValidDate(dateStr: string): Date | null {
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) {
    return null
  }
  return date
}

// Helper function to create current date
export function createCurrentDate(): Date | null {
  const now = new Date()
  if (isNaN(now.getTime())) {
    return null
  }
  return now
}

// Helper function to validate and parse date
export function validateAndParseDate(savedAt: string | undefined | null): { savedDate: Date; now: Date } | null {
  if (!isValidDateString(savedAt)) {
    return null
  }
  
  const savedDate = createValidDate(savedAt as string)
  if (!savedDate) {
    return null
  }
  
  const now = createCurrentDate()
  if (!now) {
    return null
  }
  
  return { savedDate, now }
}

// Helper function to calculate time difference in milliseconds
export function calculateTimeDiffMs(savedDate: Date, now: Date): number | null {
  const diffMs = now.getTime() - savedDate.getTime()
  if (isNaN(diffMs)) {
    return null
  }
  return diffMs
}

// Helper function to process date data for formatting
export function processDateDataForFormatting(dateData: { savedDate: Date; now: Date } | null): { diffDays: number; diffHours: number; diffMinutes: number } | null {
  if (!dateData) {
    return null
  }
  
  const { savedDate, now } = dateData
  const diffMs = calculateTimeDiffMs(savedDate, now)
  if (diffMs === null) {
    return null
  }
  
  return calculateTimeDifferences(diffMs)
}

export function formatTimeAgo(savedAt: string | undefined | null): string {
  try {
    const dateData = validateAndParseDate(savedAt)
    const timeDiffs = processDateDataForFormatting(dateData)
    if (!timeDiffs) {
      return ''
    }
    
    const { diffDays, diffHours, diffMinutes } = timeDiffs
    return formatTimeDifference(diffDays, diffHours, diffMinutes)
  } catch (error) {
    return ''
  }
}

// Helper function to format date parts
export function formatDateParts(date: Date): { day: string; month: string; year: number; hours: string; minutes: string } {
  return {
    day: String(date.getDate()).padStart(2, '0'),
    month: String(date.getMonth() + 1).padStart(2, '0'),
    year: date.getFullYear(),
    hours: String(date.getHours()).padStart(2, '0'),
    minutes: String(date.getMinutes()).padStart(2, '0'),
  }
}

// Helper function to format full date and time for tooltip
export function formatFullDateTime(savedAt: string | undefined | null): string {
  try {
    if (!isValidDateString(savedAt)) {
      return ''
    }
    
    const savedDate = createValidDate(savedAt as string)
    if (!savedDate) {
      return ''
    }
    
    const { day, month, year, hours, minutes } = formatDateParts(savedDate)
    return `${day}/${month}/${year}, ${hours}:${minutes}`
  } catch (error) {
    return ''
  }
}

// Helper functions
export function formatDate(dateStr: string | null): string {
  if (!dateStr) return 'N/A'
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return 'N/A'
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

export function formatDateTime(dateStr: string | null): string {
  if (!dateStr) return 'N/A'
  return formatDateTimeDisplay(dateStr) === 'Not set' ? 'N/A' : formatDateTimeDisplay(dateStr)
}

// Helper to capitalize first letter
export function capitalizeStatus(status: string): JobStatus {
  if (!status) return 'Bookmarked'
  return (status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()) as JobStatus
}

// Extract URL from description if it starts with "Original URL: "
export function extractUrlFromDescription(description: string | undefined): string | null {
  try {
    if (!description || typeof description !== 'string') return null
    const urlMatch = description.match(/^Original URL:\s*(.+?)(?:\n\n|$)/)
    if (urlMatch && urlMatch[1]) {
      const url = urlMatch[1].trim()
      // Add https:// if URL doesn't start with http:// or https://
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        return `https://${url}`
      }
      return url
    }
    return null
  } catch (error) {
    return null
  }
}

// Helper function to normalize hostname
export function normalizeHostname(hostname: string): string {
  return hostname.replace('www.', '')
}

// Helper function to format common job site names
export function formatJobSiteName(hostname: string): string | null {
  if (hostname.includes('linkedin.com')) {
    return 'LinkedIn'
  }
  if (hostname.includes('indeed.com')) {
    return 'Indeed'
  }
  if (hostname.includes('glassdoor.com')) {
    return 'Glassdoor'
  }
  return null
}

// Helper function to extract domain from URL using regex fallback
export function extractDomainFromUrlRegex(url: string): string {
  const match = url.match(/https?:\/\/(?:www\.)?([^/]+)/)
  if (match && match[1]) {
    const hostname = normalizeHostname(match[1])
    const formattedName = formatJobSiteName(hostname)
    return formattedName || hostname
  }
  return url.replace(/^https?:\/\//, '')
}

export function getDomainFromUrl(url: string): string {
  try {
    const urlObj = new URL(url)
    const hostname = normalizeHostname(urlObj.hostname)
    const formattedName = formatJobSiteName(hostname)
    return formattedName || hostname
  } catch (error) {
    // Fallback: try to extract domain manually
    return extractDomainFromUrlRegex(url)
  }
}

// Helper function to clean description
export function cleanDescription(description: string): string {
  return description.replace(/^Original URL:\s*.+?(?:\n\n|$)/, '').trim()
}

// Helper function to get stop words set
export function getStopWords(): Set<string> {
  return new Set([
    'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by',
    'from', 'as', 'is', 'was', 'are', 'were', 'been', 'be', 'have', 'has', 'had', 'do', 'does', 'did',
    'will', 'would', 'should', 'could', 'may', 'might', 'must', 'can', 'this', 'that', 'these', 'those',
    'i', 'you', 'he', 'she', 'it', 'we', 'they', 'me', 'him', 'her', 'us', 'them',
    'what', 'which', 'who', 'whom', 'whose', 'where', 'when', 'why', 'how',
    'all', 'each', 'every', 'both', 'few', 'more', 'most', 'other', 'some', 'such', 'no', 'nor', 'not',
    'only', 'own', 'same', 'so', 'than', 'too', 'very', 's', 't', 'can', 'will', 'just', 'don', 'should', 'now'
  ])
}

// Helper function to extract words from description
export function extractWords(description: string, stopWords: Set<string>): string[] {
  return description
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .split(/\s+/)
    .filter(word => word.length >= 2 && !stopWords.has(word))
}

// Helper function to count word frequency
export function countWordFrequency(words: string[]): Record<string, number> {
  const wordCount: Record<string, number> = {}
  words.forEach(word => {
    wordCount[word] = (wordCount[word] || 0) + 1
  })
  return wordCount
}

// Helper function to convert word count to sorted array
export function sortWordsByFrequency(wordCount: Record<string, number>, limit = 50): Array<{ word: string; count: number }> {
  return Object.entries(wordCount)
    .map(([word, count]) => ({ word, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit)
}

export function extractKeywords(description: string | undefined): Array<{ word: string; count: number }> {
  try {
    if (!description || typeof description !== 'string') return []
    
    const cleanedDescription = cleanDescription(description)
    if (!cleanedDescription) return []

    const stopWords = getStopWords()
    const words = extractWords(cleanedDescription, stopWords)
    const wordCount = countWordFrequency(words)
    return sortWordsByFrequency(wordCount)
  } catch (error) {
    return []
  }
}

// Helper function to compute effective date (same logic as JobDateChips)
export function computeEffectiveDate(
  mode: string | null | undefined,
  manualValue: string | null | undefined,
  autoValue: string | null | undefined
): string | null {
  if (mode === 'manual' && manualValue) {
    return manualValue
  }
  if (mode === 'cleared') {
    return null
  }
  // Auto mode: use the main field value
  return autoValue || null
}

export function mapSupabaseToJob(sbJob: SupabaseJob): Job {
  // Compute effective dates using the same logic as JobDateChips
  const effectiveDateApplied = computeEffectiveDate(
    sbJob.date_applied_mode,
    sbJob.date_applied_manual,
    sbJob.applied_at
  )
  
  const effectiveDeadline = computeEffectiveDate(
    sbJob.deadline_mode,
    sbJob.deadline_manual,
    sbJob.deadline_at
  )
  
  const effectiveFollowUp = computeEffectiveDate(
    sbJob.follow_up_mode,
    sbJob.follow_up_manual,
    sbJob.follow_up_at
  )

  return {
    id: sbJob.id,
    position: sbJob.title || '',
    company: sbJob.company || '',
    salary: formatSalary(sbJob.salary_max, sbJob.salary_currency),
    location: sbJob.location || 'N/A',
    status: capitalizeStatus(sbJob.status || 'bookmarked'),
    dateSaved: formatDateTime(sbJob.saved_at), // saved_at is always auto, no manual mode
    deadline: effectiveDeadline ? formatDateTime(effectiveDeadline) : 'N/A',
    dateApplied: effectiveDateApplied ? formatDateTime(effectiveDateApplied) : 'N/A',
    followUp: effectiveFollowUp ? formatDate(effectiveFollowUp) : 'Add date',
    excitement: sbJob.excitement || 0,
    description: sbJob.description || undefined,
    savedAt: sbJob.saved_at || undefined,
    source: undefined, // Not in Supabase schema yet
  }
}
